import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Box, Card, Chip, Stack, TableContainer, Tooltip, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn, SortOrder } from '../../../components/GRTable/GRTable'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useDeleteInternalBrandMutation, useGetInternalBrandsQuery } from '../../api/brands'
import { useGetInternalTagsQuery } from '../../api/tags'
import { InternalListToolbar } from '../../components/InternalListToolbar/InternalListToolbar'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../components/RowActions/RowActions'
import { BrandRow, buildBrandRows } from '../../features/brands/helpers/brandHelpers'
import { getValidationErrorMessage } from '../../helpers/apiValidationError'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

type CustomProps = { onEdit: (row: BrandRow) => void; onDelete: (row: BrandRow) => void }

export const brandEditPath = (id?: string) => `/internal/brands/edit${id ? `/${id}` : ''}`

export const BrandsPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-brands'))
  const { t } = useTranslation()
  const { notifySuccess, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const brandsQuery = useGetInternalBrandsQuery()
  const tagsQuery = useGetInternalTagsQuery()
  const [deleteBrand] = useDeleteInternalBrandMutation()
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<BrandRow>>()

  const rows = useMemo(() => buildBrandRows(brandsQuery.data ?? [], tagsQuery.data ?? []), [brandsQuery.data, tagsQuery.data])

  const tableColumns: GRTableColumn<BrandRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-analysts:brands_name'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.name || '---'}</Typography>,
        sortable: true,
        sortAccessor: ({ row }) => row.name.toLowerCase(),
        sortOrder: SortOrder.DESC,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 240 } },
      },
      {
        labelAccessor: t('internal-analysts:brands_owner'),
        accessor: ({ row }) => row.owner,
        sortable: true,
        sortAccessor: ({ row }) => row.owner.toLowerCase(),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 200 } },
      },
      {
        labelAccessor: t('internal-analysts:brands_tags'),
        accessor: ({ row }) => (
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {row.tags.map((tag) => (
              <Tooltip key={tag.id} title={tag.known ? '' : t('internal-analysts:brands_tag_unknown')}>
                <Chip size="small" label={tag.name} variant={tag.known ? 'filled' : 'outlined'} color={tag.known ? 'default' : 'warning'} />
              </Tooltip>
            ))}
          </Stack>
        ),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 360 } },
      },
      rowActionsColumn<BrandRow, CustomProps>({
        actions: ({ row, customTableProps }) => [
          { type: 'edit', label: t('internal-analysts:brands_edit'), onClick: () => customTableProps?.onEdit(row) },
          { type: 'delete', label: t('internal-analysts:brands_delete'), onClick: () => customTableProps?.onDelete(row) },
        ],
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      onEdit: (row) => navigate(brandEditPath(row.id)),
      onDelete: (row) =>
        setConfirmDialogData({
          title: t('internal-analysts:brands_delete'),
          confirmText: t('internal-analysts:brands_delete_confirm', { name: row.name || '---' }),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: row,
        }),
    }),
    [navigate, t]
  )

  const { refetch } = brandsQuery
  const handleConfirm = useCallback(
    async (confirmed?: ConfirmDialogData<BrandRow>) => {
      setConfirmDialogData(undefined)
      const row = confirmed?.data
      if (!row) return
      const name = row.name || '---'
      try {
        await deleteBrand(row.id).unwrap()
        const remaining = await refetch().unwrap()
        if (remaining.some((brand) => brand.id === row.id)) {
          notifyError(t('internal-analysts:brands_not_deleted', { name }))
          return
        }
        notifySuccess(t('internal-analysts:brands_deleted', { name }))
      } catch (error) {
        const reason = getValidationErrorMessage(error)
        notifyError(reason ? t('internal-analysts:brands_delete_failed_reason', { name, reason }) : t('internal-analysts:brands_delete_failed', { name }))
      }
    },
    [deleteBrand, notifyError, notifySuccess, refetch, t]
  )

  return (
    <Box>
      <InternalListToolbar add={{ label: t('internal-analysts:brands_add'), onClick: () => navigate(brandEditPath()) }} />

      {tagsQuery.isError && (
        <Typography color="error.main" mb={2}>
          {t('internal-analysts:brands_tags_load_failed')}
        </Typography>
      )}

      <QueryStateBoundary
        isLoading={brandsQuery.isLoading || tagsQuery.isLoading}
        isError={brandsQuery.isError}
        errorText={t('internal-analysts:brands_load_failed')}
        skeletonHeight={240}
      >
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey="id"
            customProps={customProps}
            noRowsLabel={t('internal-analysts:brands_none')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
    </Box>
  )
}
